import { Link } from "react-router-dom"
import useTargetStorage from "@/hooks/useTargetStorage"
import Callout from "components/callout/callout"
import { useTranslation } from "react-i18next"
import { textToHTML } from "utils/converters"
import Section from "components/layout/section"
import useStorages from "hooks/useStorages"
import IconImage from "components/image/IconImage"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import DocList, { ViewMode } from "components/list/docList/docList"
import StoragesFilter from "./storagesFilter"
import Doc from "components/list/docList/doc"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import TableList from "components/list/tableList/tableList"
import Button from "components/inputs/button"
import { ReactComponent as CelebrationIcon } from "assets/icons/celebration.svg"
import { DeviceSize, FormStatus } from "types"
import useWindowDimensions from "@/hooks/useWindowDimensions"

const StoragesList = () => {

    const { t } = useTranslation()
    const { deviceSize } = useWindowDimensions()
    const { setTargetStorage } = useTargetStorage()

    const {
        storages,
        filteredStorages,
        storagesStatus,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted
    } = useStorages()

    // Handle errors.
    if (storagesStatus === FormStatus.ERROR) return <Callout type={`error`}>{t('errorLoadingStorages')}</Callout>

    if (storagesStatus === FormStatus.SUCCESS && storages.length === 0) {
        return (
            <Section design={`card-centered`}>
                <IconImage>
                    <CelebrationIcon />
                </IconImage>
                <h2>{t(`titleGettingStarted`)}</h2>
                <p>{t('storagesGettingStartedText')}</p>
                <Link className="block w-full md:inline-block md:w-fit" to={`/storages/create`}><Button wideOnMobile design="primary"><AddIcon /><span>{t('buttonNewStorage')}</span></Button></Link>
            </Section>
        )
    }

    // Render storages.
    return (
        <DocList
            filterElement={
                <StoragesFilter
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    sortDirection={sortDirection}
                    setSortDirection={setSortDirection}
                    filterQuery={filterQuery}
                    setFilterQuery={setFilterQuery}
                    displayDeleted={displayDeleted}
                    setDisplayDeleted={setDisplayDeleted}
                    isLoading={storagesStatus === FormStatus.LOADING}
                />
            }
            id={`storage_list`}
            viewModes={deviceSize == DeviceSize.SM ? [ViewMode.List] : [ViewMode.List, ViewMode.Table]}
            defaultViewMode={ViewMode.List}
            showResults={(storagesStatus === FormStatus.SUCCESS && filteredStorages.length > 0) || storagesStatus !== FormStatus.SUCCESS}
        >
            <div data-view-mode={ViewMode.Table}>
                <TableList>
                    <TableListRow isHeader={true}>
                        <TableListCell>{t(`columnName`)}</TableListCell>
                        <TableListCell>{t(`columnLocation`)}</TableListCell>
                        <TableListCell>{t(`columnDescription`)}</TableListCell>
                    </TableListRow>
                    {storagesStatus === FormStatus.SUCCESS && filteredStorages.length > 0
                        ? filteredStorages.map((storageDocData) => {
                            const storageId = storageDocData.id as string;
                            return (
                                <TableListRow key={storageId} to="/storage" onClick={() => setTargetStorage(storageId, storageDocData.name)} isDeleted={storageDocData.isDeleted}>
                                    <TableListCell>{storageDocData.name}</TableListCell>
                                    <TableListCell>{storageDocData.location || '-'}</TableListCell>
                                    <TableListCell>
                                        {storageDocData.description ? (
                                            <span dangerouslySetInnerHTML={{__html: textToHTML(storageDocData.description)}}></span>
                                        ) : (
                                            '-'
                                        )}
                                    </TableListCell>
                                </TableListRow>
                            )
                        }) :
                        Array.from({ length: 3 }).map((_, _index) => (
                            <TableListRow key={_index}>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                                <TableListCell><p className="skeleton"></p></TableListCell>
                            </TableListRow>
                        ))
                    }
                </TableList>
            </div>
            <div data-view-mode={ViewMode.List} className="flex flex-col gap-4">
                {storagesStatus === FormStatus.SUCCESS && filteredStorages.length > 0
                    ? filteredStorages.map((storageDocData) => {
                        const storageId = storageDocData.id as string;
                        return (
                            <Doc key={storageId} title={storageDocData.name} description={storageDocData.description} to="/storage" onClick={() => setTargetStorage(storageId, storageDocData.name)} isDeleted={storageDocData.isDeleted}>
                                {storageDocData.description && <p dangerouslySetInnerHTML={{__html: textToHTML(storageDocData.description)}}></p>}
                                {storageDocData.location && <p><b>Location</b>: {storageDocData.location}</p>}
                            </Doc>
                        )
                    }) :
                    Array.from({ length: 3 }).map((_, _index) => (
                        <Doc key={_index} isLoading={true}>
                            <p className="skeleton"></p>
                            <p className="skeleton"></p>
                            <Button design={`skeleton`}></Button>
                        </Doc>
                    ))
                }
            </div>
        </DocList>
    )

}

export default StoragesList

